import { httpClient as http } from '@/utils/http'
import type { PageRequest } from '@/types/api'

// API基础路径
const API_BASE = '/mdm/currency'

// ==================== 通用类型定义 ====================

// 分页响应类型
export interface PageResponse<T> {
  pageNum: number // 当前页码
  pageSize: number // 每页大小
  total: number // 总记录数
  pages: number // 总页数
  records: T[] // 数据列表
}

// API统一响应格式
export interface ApiResponse<T> {
  code: number
  message: string
  data: T
  success: boolean
}

// ==================== 币种相关类型定义 ====================

// 币种分页查询请求
export interface CurrencyPageRequest extends PageRequest {
  keyword?: string // 关键字搜索
  currencyCode?: string // 币种代码，如 USD、CNY、EUR
  currencyNameCn?: string // 币种中文名称，如 美元、人民币、欧元
  currencyNameEn?: string // 币种英文名称，如 United States Dollar、Chinese Yuan、Euro
  currencySymbol?: string // 币种符号，如 $、¥、€
  enabled?: number // 启用状态：1-启用, 0-禁用
  tenantId?: string // 租户ID
}

// 币种列表项视图对象
export interface CurrencyListVO {
  id: string // 主键ID
  currencyCode: string // 币种代码，如 USD、CNY、EUR
  currencyNameCn: string // 币种中文名称，如 美元、人民币、欧元
  currencyNameEn?: string // 币种英文名称，如 United States Dollar、Chinese Yuan、Euro
  currencySymbol?: string // 币种符号，如 $、¥、€
  decimalPlaces?: string // 币种小数位数代码，如 2表示2位小数
  exchangeRateDecimalPlaces?: string // 汇率小数位数代码，如 6表示6位小数
  roundingRuleCode?: string // 进位规则代码：ROUND_HALF_UP-四舍五入, ROUND_UP-向上进位, ROUND_DOWN-向下进位
  enabled: number // 启用状态：1-启用, 0-禁用
  dateCreated: string // 创建时间
  dateModified: string // 更新时间
}

// 币种详情视图对象
export interface CurrencyVO {
  id: string // 主键ID
  tenantId?: string // 租户ID
  currencyCode: string // 币种代码，如 USD、CNY、EUR
  currencyNameCn: string // 币种中文名称，如 美元、人民币、欧元
  currencyNameEn?: string // 币种英文名称，如 United States Dollar、Chinese Yuan、Euro
  currencySymbol?: string // 币种符号，如 $、¥、€
  decimalPlaces?: string // 币种小数位数代码，如 2表示2位小数
  exchangeRateDecimalPlaces?: string // 汇率小数位数代码，如 6表示6位小数
  roundingRuleCode?: string // 进位规则代码：ROUND_HALF_UP-四舍五入, ROUND_UP-向上进位, ROUND_DOWN-向下进位
  enabled: number // 启用状态：1-启用, 0-禁用
  dateCreated: string // 创建时间
  dateModified: string // 更新时间
}

// 币种保存请求
export interface CurrencySaveRequest {
  id?: string // 主键ID（更新时使用）
  currencyCode: string // 币种代码，如 USD、CNY、EUR
  currencyNameCn: string // 币种中文名称，如 美元、人民币、欧元
  currencyNameEn?: string // 币种英文名称，如 United States Dollar、Chinese Yuan、Euro
  currencySymbol?: string // 币种符号，如 $、¥、€
  decimalPlaces?: string // 币种小数位数代码，如 2表示2位小数
  exchangeRateDecimalPlaces?: string // 汇率小数位数代码，如 6表示6位小数
  roundingRuleCode?: string // 进位规则代码：ROUND_HALF_UP-四舍五入, ROUND_UP-向上进位, ROUND_DOWN-向下进位
  enabled?: number // 启用状态：1-启用, 0-禁用
  tenantId?: string // 租户ID
}

// 模糊搜索请求
export interface FuzzySearchRequest {
  keyword: string // 搜索关键词
  enabled?: number // 启用状态：1-启用, 0-禁用
  tenantId?: string // 租户ID
}

// 批量删除请求
export interface BatchDeleteRequest {
  ids: string[] // ID列表
}

// 批量状态更新请求
export interface BatchUpdateStatusRequest {
  ids: string[] // ID列表
  enabled: number // 启用状态：1-启用, 0-禁用
}

// 币种代码查询请求
export interface CurrencyCodeQueryRequest {
  currencyCode: string // 币种代码
}

// 批量关联请求
export interface BatchAssociateRequest {
  platformIds: string[] // 平台数据ID列表
}

// 批量解除关联请求
export interface BatchDisassociateRequest {
  ids: string[] // ID列表
}

// ==================== API客户端对象 ====================

// 币种API
export const currencyApi = {
  // 分页查询币种
  page: (params: CurrencyPageRequest) => {
    return http.post<PageResponse<CurrencyListVO>>(`${API_BASE}/queryPage`, params)
  },

  // 查询币种列表（不分页）
  list: (params: CurrencyPageRequest) => {
    return http.post<CurrencyListVO[]>(`${API_BASE}/queryList`, params)
  },

  // 模糊搜索币种
  search: (params: FuzzySearchRequest) => {
    return http.post<CurrencyListVO[]>(`${API_BASE}/search`, params)
  },

  // 根据ID获取币种详情
  getById: (params: { id: string }) => {
    return http.post<CurrencyVO>(`${API_BASE}/getById`, params)
  },

  // 保存币种（新增或更新）
  save: (params: CurrencySaveRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 删除币种
  delete: (params: { id: string }) => {
    return http.post<boolean>(`${API_BASE}/delete`, params)
  },

  // 批量保存币种
  batchSave: (params: CurrencySaveRequest[]) => {
    return http.post<boolean>(`${API_BASE}/batchSave`, params)
  },

  // 批量删除币种
  batchDelete: (params: BatchDeleteRequest) => {
    return http.post<boolean>(`${API_BASE}/batchDelete`, params)
  },

  // 更新币种状态
  updateStatus: (params: BatchUpdateStatusRequest) => {
    return http.post<boolean>(`${API_BASE}/updateStatus`, params)
  },

  // 批量更新币种状态
  batchUpdateStatus: (params: BatchUpdateStatusRequest) => {
    return http.post<boolean>(`${API_BASE}/batchUpdateStatus`, params)
  },

  // 根据币种代码查询币种
  getByCurrencyCode: (params: CurrencyCodeQueryRequest) => {
    return http.post<CurrencyVO>(`${API_BASE}/getByCode`, params)
  },

  // 查询启用状态的币种列表
  queryEnabled: (params: CurrencyPageRequest) => {
    return http.post<CurrencyListVO[]>(`${API_BASE}/queryEnabled`, params)
  },

  // 查询可关联的平台币种（分页）
  queryPlatformPage: (params: CurrencyPageRequest) => {
    return http.post<PageResponse<CurrencyListVO>>(`${API_BASE}/queryPlatformPage`, params)
  },

  // 查询可关联的平台币种（列表）
  queryPlatformList: (params: CurrencyPageRequest) => {
    return http.post<CurrencyListVO[]>(`${API_BASE}/queryPlatformList`, params)
  },

  // 批量关联平台币种
  batchAssociate: (params: BatchAssociateRequest) => {
    return http.post<boolean>(`${API_BASE}/batchAssociate`, params)
  },

  // 批量解除关联平台币种
  batchDisassociate: (params: BatchDisassociateRequest) => {
    return http.post<boolean>(`${API_BASE}/batchDisassociate`, params)
  }
}
